import { LabeledValue, LabeledValueWithButton } from "@msys/ui";
import { Functions as FunctionsIcon } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import React from "react";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { FormattedFloatField } from "../../commons/form-fields/FormattedFloatField.js";
import { AttributeExpressionModal } from "./modals/AttributeExpressionModal.js";

type ExpressionResult =
  | { __type: "missing-value" }
  | { __type: "value"; value: unknown }
  | { __type: "no_expr" }
  | { __type: "error"; message: string };

interface Expression {
  attribute: string;
  expr: string;
  result: ExpressionResult;
}

interface Props extends React.ComponentProps<typeof FormattedFloatField> {
  projectId: string | null;
  docId: string;
  itemId: string;
  name: string;
  expression: Expression | undefined;
  hideExpressionIcon?: boolean;
  label?: string;
}

export function FormattedFloatWithExpressionField({
  projectId,
  docId,
  itemId,
  name,
  expression,
  hideExpressionIcon,
  size,
  ...props
}: Props) {
  const formikProps = useFormikContext();
  const value = formikProps.getFieldProps(name).value;

  const [isEditing, setIsEditing] = React.useState(false);
  const [isExpressionModalOpen, setExpressionModalOpen] = React.useState(false);
  const expressionButtonRef = React.useRef<HTMLButtonElement>(null);

  // `result` arrives from the `JSON` scalar as `any`, so guard the read.
  const hasExpressionRefError = expression?.result?.__type === "missing-value";

  if (isEditing) {
    return (
      <Stack
        direction={"row"}
        spacing={0.5}
        sx={{
          alignItems: "center",
        }}
      >
        <FormattedFloatField
          {...props}
          size={size}
          name={name}
          autoFocus
          onBlur={event => {
            if (event.relatedTarget !== expressionButtonRef.current) {
              setIsEditing(false);
            }
          }}
        />
        <RestrictedByCapabilityWithDebug capability="EXPRESSIONS">
          <IconButton
            size="extra-small"
            color="secondary"
            onClick={() => {
              setExpressionModalOpen(true);
              setIsEditing(false);
            }}
            ref={expressionButtonRef}
          >
            <FunctionsIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </RestrictedByCapabilityWithDebug>
      </Stack>
    );
  }

  const valueContent = hasExpressionRefError ? (
    <Typography color="error">{"Reference error"}</Typography>
  ) : (
    value
  );

  return (
    <>
      {expression ? (
        <RestrictedByCapabilityWithDebug
          capability="EXPRESSIONS"
          otherwise={
            <LabeledValue
              label={props.label ?? ""}
              labelIcon={hideExpressionIcon ? undefined : FunctionsIcon}
            >
              {valueContent}
            </LabeledValue>
          }
        >
          <LabeledValueWithButton
            size={size}
            label={props.label ?? ""}
            labelIcon={hideExpressionIcon ? undefined : FunctionsIcon}
            onClick={() => {
              setExpressionModalOpen(true);
            }}
          >
            {valueContent}
          </LabeledValueWithButton>
        </RestrictedByCapabilityWithDebug>
      ) : (
        <LabeledValueWithButton
          size={size}
          label={props.label ?? ""}
          onClick={() => {
            setIsEditing(true);
          }}
        >
          {valueContent}
        </LabeledValueWithButton>
      )}
      {isExpressionModalOpen && (
        <AttributeExpressionModal
          projectId={projectId}
          docId={docId}
          itemId={itemId}
          fieldName={name}
          expression={expression?.expr}
          handleClose={() => {
            setExpressionModalOpen(false);
          }}
        />
      )}
    </>
  );
}
